/**
 * Auth context provider + `useAuth` hook (R-18.197/R-18.200/R-18.201).
 *
 * No third-party auth library (R-18.197): auth is an `AuthProvider` + `useAuth` +
 * the shared Axios interceptor (`shared/api/client.ts`). Token transport is the M7
 * cookie flow (R-18.198/R-18.199, DEC-026): the browser holds the HttpOnly access +
 * refresh cookies, so this layer NEVER reads or writes a token. "Am I authenticated"
 * is derived purely from session state — the `GET /auth/me` query (R-18.202): a 200
 * means there is a live session, a 401 means there is not. There is no localStorage
 * read/write anywhere (R-18.199).
 *
 * Provider placement (R-18.200): mounted inside `QueryClientProvider` (it uses
 * TanStack Query) and outside `RouterProvider` (routes/`<ProtectedRoute>` read auth
 * state) — see App.tsx.
 *
 * Contract surface (R-18.201): `useAuth()` throws outside the provider and returns
 * `{ user, isAuthenticated, isLoading, login, logout }`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import type { ReactNode } from 'react';

import {
  SESSION_REVOKED_CODE,
  apiClient,
  attemptSessionRefresh,
  setCsrfToken,
  setMfaRequiredListener,
} from '@/shared/api/client';

/**
 * The current-user shape returned by `GET /auth/me` (`{ user }` → the backend
 * `CurrentUser` view). The public projection excludes `password_hash` (R-9.54) and now
 * carries the actor's `role` plus the EFFECTIVE capability-key set (explicit grants ∪
 * descendants — DEC-021), sourced from the server (PROD-004). The SPA uses `capabilities`
 * for defense-in-depth UI gating (PA-web-capability-gating.1); it never replaces the
 * backend's own 403 enforcement (PA-web-capability-gating.2 / R-18.209). `fullName` is the
 * optional display name the header pill renders (S-header-user-pill); it may be null, in
 * which case the UI falls back to the email.
 */
export interface CurrentUser {
  id: string;
  email: string;
  fullName: string | null;
  /**
   * The media object holding the hand-drawn signature this user set in their own profile (act
   * e-signing), or null when they have not drawn one. The profile page previews it, and an act's
   * signature panel reads it to decide between "sign" and "create a signature and sign".
   */
  signatureMediaId: string | null;
  isActive: boolean;
  totpEnrolled: boolean;
  createdAt: string;
  role: string;
  capabilities: string[];
  /**
   * The live 2FA policy for this session (plan R2 §6), from the server's session gate:
   * `required` - 2FA is mandatory for the user now (the policy flag is on and the user holds one
   * of the roles it covers); `sessionVerified` - this session was opened with a code; `enrolled` -
   * an authenticator is set up. `required && !sessionVerified` keeps the user on the 2FA screen.
   * Optional only so fixtures written before R2 still type; the API always sends it.
   */
  mfa?: CurrentUserMfa;
}

/** The `/auth/me` 2FA block (plan R2 §6). */
export interface CurrentUserMfa {
  required: boolean;
  sessionVerified: boolean;
  enrolled: boolean;
}

/**
 * Whether the session may only reach the 2FA screen: 2FA is mandatory for the user and this
 * session was not opened with a code (the server answers anything else with 403 MFA_REQUIRED).
 */
export function isMfaPending(user: CurrentUser | null | undefined): boolean {
  return user?.mfa?.required === true && !user.mfa.sessionVerified;
}

/** Whether 2FA is mandatory for the user right now (the profile then hides "Disable 2FA"). */
export function isMfaMandatory(user: CurrentUser | null | undefined): boolean {
  return user?.mfa?.required === true;
}

/** Login credentials — email + password only. A TOTP-enrolled account completes its
 * second factor at a SEPARATE step (the two-step 2FA challenge, PROD-011): the inline
 * code field is gone (see LoginPage / TwoFactorForm). */
export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * The outcome of the password step. `ok` = a session was established (non-enrolled
 * account); `2fa_required` = the password passed but a TOTP-enrolled account must complete
 * the second factor at the verify step, carrying the returned short-lived `challengeToken`.
 */
export type LoginResult = { status: 'ok' } | { status: '2fa_required'; challengeToken: string };

export interface AuthContextValue {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  /** Complete a two-step 2FA login with the challenge token + the 6-digit code. */
  verifyLoginChallenge: (challengeToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Whether the current user's effective capability set includes `key` — the
   * defense-in-depth gate the SPA uses to hide nav entries / controls (PROD-004 /
   * PA-web-capability-gating.1). Always false when logged out. This NEVER substitutes for
   * backend enforcement (the server still 403s) — it only avoids showing a control the
   * action behind which would be denied (PA-web-capability-gating.2 / R-18.209).
   */
  hasCapability: (key: string) => boolean;
}

/** The TanStack Query key for the current-user session probe (R-18.203). */
export const AUTH_ME_QUERY_KEY = ['auth', 'me'] as const;

// Exported so the test harness (`@/test/render`) can supply a fixed auth context for
// component tests without standing up the real /auth/me probe; app code uses `useAuth`.
export const AuthContext = createContext<AuthContextValue | null>(null);

interface MeResponse {
  user: CurrentUser;
}

// The bare session probe. Flagged `skipAuthRefresh` so the shared client's 401
// interceptor neither auto-refreshes nor hard-redirects on it — the caller below owns
// the refresh-and-retry decision.
async function fetchMe(): Promise<CurrentUser> {
  const { data } = await apiClient.get<MeResponse>('/auth/me', {
    meta: { skipAuthRefresh: true },
  });
  return data.user;
}

// The /auth/login response is a discriminated union (DEC-026 + PROD-011): a session
// success carries the readable `csrfToken` (seeded into the client's in-memory CSRF token
// so the next mutation echoes it without a Set-Cookie read race); a `2fa_required` outcome
// carries the short-lived `challengeToken` and sets NO cookies. The access/refresh tokens
// are echoed only for the legacy header-bearer client; the SPA ignores them (R-18.199).
interface LoginSuccessResponse {
  status?: 'ok';
  csrfToken: string;
}
interface LoginChallengeResponse {
  status: '2fa_required';
  challengeToken: string;
}
type LoginApiResponse = LoginSuccessResponse | LoginChallengeResponse;

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const queryClient = useQueryClient();

  // R-18.202 — fetch the current user on startup. The browser sends the HttpOnly
  // cookie automatically; a 401 (no/expired session) resolves to `null` rather than
  // throwing, so an unauthenticated visitor renders the login screen cleanly.
  //
  // A 401 probe does NOT immediately mean "logged out": the short-lived access cookie
  // expires between page loads while the 14-day refresh cookie is still live (any
  // reload / new tab past the access TTL used to land on the login screen). So on a
  // 401 we attempt ONE silent refresh and re-probe; only when the refresh itself fails
  // is the visitor logged out. attemptSessionRefresh never hard-redirects — a `false`
  // maps to `null` here and the declarative <ProtectedRoute> does the routing, which
  // is what keeps the logged-out probe from looping refresh→401→redirect→re-probe
  // (MF1). `retry: false` avoids hammering /me when logged out.
  const { data: user = null, isLoading } = useQuery<CurrentUser | null>({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: async () => {
      try {
        return await fetchMe();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 401) {
          // A revoked session (plan R2 §4.4) has no live refresh token left: the client already
          // ended it, and a refresh attempt would only replay a revoked cookie.
          const code = (error.response.data as { error?: { code?: unknown } } | undefined)?.error
            ?.code;
          if (code === SESSION_REVOKED_CODE) {
            return null;
          }
          if (await attemptSessionRefresh()) {
            try {
              return await fetchMe();
            } catch {
              return null;
            }
          }
        }
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // A 403 MFA_REQUIRED anywhere (plan R2 §6) means the policy now covers this session: refetch
  // /auth/me so <ProtectedRoute> swaps the shell for the 2FA screen. When the cached user already
  // says so, there is nothing new to learn - skipping the refetch is what keeps a request that keeps
  // answering 403 from turning into a /auth/me loop.
  useEffect(() => {
    setMfaRequiredListener(() => {
      const current = queryClient.getQueryData<CurrentUser | null>(AUTH_ME_QUERY_KEY);
      if (isMfaPending(current)) return;
      void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    });
    return () => setMfaRequiredListener(null);
  }, [queryClient]);

  // The password step. A non-enrolled account: the backend sets the HttpOnly cookies + a
  // readable csrf_token cookie (DEC-026); we seed the returned CSRF token and refetch
  // /auth/me so `isAuthenticated` flips. A TOTP-enrolled account: the backend returns
  // `2fa_required` + a short-lived challenge token and sets NO cookies — we establish
  // nothing and hand the challenge back to the caller (LoginPage routes to the OTP step).
  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials): Promise<LoginResult> => {
      const { data } = await apiClient.post<LoginApiResponse>('/auth/login', credentials);
      if (data.status === '2fa_required') {
        return { status: '2fa_required', challengeToken: data.challengeToken };
      }
      setCsrfToken(data.csrfToken);
      // R-18.203 — refetch the user after login so `isAuthenticated` flips to true.
      await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
      return { status: 'ok' };
    },
  });

  // The second step of a two-step 2FA login (PROD-011): exchange the challenge token + the
  // 6-digit code for a session, then seed the CSRF token and refetch /auth/me. The backend
  // sets the HttpOnly cookies on success exactly as the single-step path does.
  const verifyMutation = useMutation({
    mutationFn: async ({ challengeToken, code }: { challengeToken: string; code: string }) => {
      const { data } = await apiClient.post<LoginSuccessResponse>('/auth/login/verify', {
        challengeToken,
        totpCode: code,
      });
      setCsrfToken(data.csrfToken);
      await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    },
  });

  const login = useCallback(
    (credentials: LoginCredentials) => loginMutation.mutateAsync(credentials),
    [loginMutation],
  );

  const verifyLoginChallenge = useCallback(
    async (challengeToken: string, code: string) => {
      await verifyMutation.mutateAsync({ challengeToken, code });
    },
    [verifyMutation],
  );

  const logout = useCallback(async () => {
    // R-18.204 — POST /auth/logout so the SERVER expires the HttpOnly cookies; the SPA
    // cannot clear them itself (it never holds a token — R-18.199), and without the
    // server call the next /auth/me would silently re-authenticate. The CSRF header is
    // attached by the client's request interceptor from the in-memory/cookie token. We
    // best-effort the call (a network failure should not strand the user in a logged-in
    // UI), then drop the seeded CSRF token and the session probe + query cache to fall
    // back to the unauthenticated state.
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Ignore — proceed to clear local state regardless.
    }
    setCsrfToken(null);
    queryClient.setQueryData(AUTH_ME_QUERY_KEY, null);
    queryClient.clear();
  }, [queryClient]);

  // Defense-in-depth capability check (PROD-004): true only when the server-sourced
  // effective set includes `key`. Logged-out → always false (no user, no capabilities).
  const hasCapability = useCallback(
    (key: string): boolean => user?.capabilities.includes(key) ?? false,
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      login,
      verifyLoginChallenge,
      logout,
      hasCapability,
    }),
    [user, isLoading, login, verifyLoginChallenge, logout, hasCapability],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Read the auth context (R-18.201). Throws when used outside `<AuthProvider>` so a
 * misplacement is a loud failure, not a silent `null`.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
